import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import VersionService from '../services/VersionService';

const initialState = {
  datas: [],
  pagination: null,
  formError: [],
  isSuccess: false,
  submitLoading: false,
  loading: false,
  isOpenDeleteDialog: false,
  selectedVersion: null,
  error: null,
};

export const fetchVersionList = createAsyncThunk(
  'version/fetchVersionList',
  async (data = {}, { rejectWithValue }) => {
    try {
      const resp = await VersionService.List(data);
      return resp;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const createVersion = createAsyncThunk(
  'version/createVersion',
  async (data, { rejectWithValue }) => {
    try {
      const response = await VersionService.Create(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const updateVersion = createAsyncThunk(
  'version/updateVersion',
  async (data, { rejectWithValue }) => {
    try {
      const response = await VersionService.Put(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteVersion = createAsyncThunk(
  'version/deleteVersion',
  async (id, { rejectWithValue }) => {
    try {
      const response = await VersionService.Delt(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const versionSlice = createSlice({
  name: 'version',
  initialState,
  reducers: {
    openDeleteDialog: (state, action) => {
      state.isOpenDeleteDialog = action.payload;
      if (!action.payload) {
        state.selectedVersion = null;
      }
    },
    setSelectedVersion: (state, action) => {
      state.selectedVersion = action.payload;
    },
    resetFormState: (state) => {
      state.isSuccess = false;
      state.formError = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchVersionList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVersionList.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.status === 200) {
          state.datas = action.payload.data?.data?.version_controls ?? [];
          state.pagination = action.payload.data?.data?.pagination ?? null;
        } else {
          state.error = action.payload?.data?.message || 'Failed to fetch app versions';
          toast.error(state.error);
        }
      })
      .addCase(fetchVersionList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Error fetching app versions';
        toast.error(state.error);
      })

      // Create
      .addCase(createVersion.pending, (state) => {
        state.submitLoading = true;
        state.isSuccess = false;
        state.formError = [];
      })
      .addCase(createVersion.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = true;
        if (action.payload?.data?.data) {
          state.datas.unshift(action.payload.data.data);
          if (state.pagination) {
            state.pagination.total = (state.pagination.total || 0) + 1;
          }
        }
        toast.success(action.payload?.data?.message || 'App version created successfully');
      })
      .addCase(createVersion.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        const errors = action.payload?.data?.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data?.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })

      // Update
      .addCase(updateVersion.pending, (state) => {
        state.submitLoading = true;
        state.isSuccess = false;
        state.formError = [];
      })
      .addCase(updateVersion.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = true;
        const updated = action.payload?.data?.data;
        if (updated) {
          const index = state.datas.findIndex((b) => b.id === updated.id);
          if (index !== -1) {
            state.datas[index] = updated;
          }
        }
        toast.success(action.payload?.data?.message || 'App version updated successfully');
      })
      .addCase(updateVersion.rejected, (state, action) => {
        state.submitLoading = false;
        state.isSuccess = false;
        const errors = action.payload?.data?.errors;
        if (isEmpty(errors)) {
          toast.error(action.payload?.data?.message || 'Something went wrong');
        } else {
          state.formError = errors;
        }
      })

      // Delete
      .addCase(deleteVersion.pending, (state) => {
        state.submitLoading = true;
      })
      .addCase(deleteVersion.fulfilled, (state, action) => {
        state.submitLoading = false;
        const deletedId = action.meta.arg;
        state.datas = state.datas.filter((item) => item.id !== deletedId);
        if (state.pagination && state.pagination.total > 0) {
          state.pagination.total -= 1;
        }
        state.isOpenDeleteDialog = false;
        state.selectedVersion = null;
        toast.success(action.payload?.data?.message || 'App version deleted successfully');
      })
      .addCase(deleteVersion.rejected, (state, action) => {
        state.submitLoading = false;
        toast.error(action.payload?.data?.message || 'Something went wrong');
      });
  },
});

export const { openDeleteDialog, setSelectedVersion, resetFormState } = versionSlice.actions;
export const versionSelector = (state) => state.version;
export default versionSlice.reducer;
